import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency } from '../utils/format';

function StatCard({ label, value, sub, accent }) {
  const displayValue = value ?? '—';
  const valueLength = String(displayValue).length;
  const fontSize = valueLength > 12 ? '1.25rem' : valueLength > 9 ? '1.5rem' : '1.9rem';

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E2E5EC',
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: '18px 22px',
      flex: '1 1 160px',
      minWidth: 150,
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(2,19,68,0.06)',
    }}>
      <div style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        color: '#6B7A99',
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        fontSize,
        fontWeight: 600,
        color: '#021344',
        lineHeight: 1.2,
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}>
        {displayValue}
      </div>
      {sub && (
        <div style={{ fontSize: '0.75rem', color: '#9AA3B5', marginTop: 5 }}>{sub}</div>
      )}
    </div>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sub: PropTypes.string,
  accent: PropTypes.string.isRequired,
};

/**
 * Tarjetas KPI del resumen mensual o anual.
 * En el resumen anual se agrega la tarjeta de clientes nuevos (creados en el año).
 */
function SummaryCards({ summary, period = 'month' }) {
  if (!summary) return null;
  const isYear = period === 'year';

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      <StatCard label={isYear ? 'Remates del año' : 'Remates del mes'} value={summary.total_auctions} accent="#1F50FF" />
      <StatCard label="Total de lotes"          value={summary.total_lots}           accent="#2563EB" />
      <StatCard label="Total participantes"     value={summary.total_participants}   accent="#16A34A" />
      <StatCard label="Prom. participantes"     value={summary.avg_participants}     accent="#FF9900" />
      <StatCard label="Total vendido"           value={formatCurrency(summary.total_sold)}       accent="#16A34A" />
      <StatCard label="Total comisión"          value={formatCurrency(summary.total_commission)} accent="#1F50FF" />
      <StatCard
        label="Prom. no adjudicados"
        value={`${summary.avg_unadjudicated_pct ?? '—'}%`}
        sub="lotes no adjudicados por remate"
        accent="#DC2626"
      />
      {isYear && (
        <StatCard
          label="Clientes nuevos"
          value={summary.new_tenants}
          sub="clientes creados en el año"
          accent="#7C3AED"
        />
      )}
    </div>
  );
}

SummaryCards.propTypes = {
  summary: PropTypes.shape({
    total_auctions:        PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_lots:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_participants:    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_participants:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_sold:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_commission:      PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_unadjudicated_pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    new_tenants:           PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  period: PropTypes.oneOf(['month', 'year']),
};

export default SummaryCards;
